import { html } from 'lit';
import {
  aTimeout,
  expect,
  waitUntil,
  elementUpdated,
  fixture,
} from '@open-wc/testing';
import { executeServerCommand, setViewport } from '@web/test-runner-commands';
import fetchMock from 'fetch-mock';
import mockData from './mockData.json';
import type { NotificationsTestHarness } from '../../src/testHarness.js';
import '../../src/testHarness.js';

export const apiCallKey = 'getNotifications';

export const getMockData = () => {
  const fiveDaysAgo = new Date(
    new Date().setDate(new Date().getDate() - 5)
  ).toISOString();
  const sixtyTwoDaysAgo = new Date(
    new Date().setDate(new Date().getDate() - 62)
  ).toISOString();
  const modifiedMockItems = mockData.items.map(item => ({
    ...item,
    created_at: fiveDaysAgo,
  }));
  modifiedMockItems[0].created_at = new Date().toISOString();
  modifiedMockItems[1].created_at = sixtyTwoDaysAgo;
  return { ...mockData, items: modifiedMockItems };
};

export const mockDataMeta = {
  itemsCount: 11,
  todayItemsCount: 1,
  earlierItemsCount: 10,
  unreadCount: 9,
  readCount: 2,
  incidentsCount: 2,
  dcimAlertsCount: 1,
  serviceTicketsCount: 3,
  plannedMaintenanceCount: 5,
};

export type TelemInfo = {
  tag: string;
  text: string;
  value: string;
  isVisible: boolean;
  attributes: { [key: string]: string | boolean };
};

export const fixtureMockApi = () => {
  fetchMock.mockGlobal().route(
    /notifications\/notifications\/*/,
    args => {
      // console.log(args);
      const { options } = args;

      if (options.method === 'options') {
        return { status: 200 };
      }

      if (options.method === 'delete') {
        return { status: 200 };
      }

      if (options.method === 'patch') {
        return { status: 200 };
      }

      return {
        body: getMockData(),
        headers: { 'x-total-unread-count': mockDataMeta.unreadCount },
      };
    },
    apiCallKey
  );
};

export const fixtureBeforeEach = async () => {
  fixtureMockApi();

  const el = await fixture<NotificationsTestHarness>(
    html`<notifications-test-harness></notifications-test-harness>`
  );

  let drawInfo: TelemInfo[] = await executeServerCommand('mouseFocusInfo', {
    selector: '[data-testid="notifications-drawer"]',
    action: 'info',
  });

  await elementUpdated(el);

  if (!drawInfo[0].attributes.isvisible) {
    expect(drawInfo[0].attributes.isvisible).to.equal(undefined);
    window.dispatchEvent(new CustomEvent('ix-notifications-toggle-drawer'));

    await waitUntil(
      async () => {
        drawInfo = await executeServerCommand('mouseFocusInfo', {
          selector: '[data-testid="notifications-drawer"]',
          action: 'info',
        });

        return drawInfo[0].attributes.isvisible;
      },
      'Drawer is never visible',

      { interval: 10, timeout: 20000 }
    );
  }

  expect(drawInfo[0].attributes.isvisible).to.equal(true);

  await aTimeout(300);

  return el;
};

export const fixtureAfterEach = () => {
  fetchMock.hardReset({ includeSticky: true });
};

export const fixtureBefore = async () => {
  await setViewport({ width: 1200, height: 1000 });
};

export const screenshotAndCompare = async (name: string) => {
  await aTimeout(300); // this lets the UI settle and prevents pixel diff's from slight differences in speed
  await executeServerCommand('take-screenshot', name);
  await executeServerCommand('pixel-match', name);
};
